import { Action, Dispatch, Middleware } from 'redux';
import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '..';
import { setWorkingChanges } from '../actions/appActions';
import { createDoWorkingChangesExistOnComponentDesign } from '../selectors/workingChangesSelectors';

const isActiveAnalysisSetCurrentSelector = createSelector(
  (state: RootState) => state.analysisSets,
  (state: RootState) => state.componentDesigns.data,
  (analysisSets, componentDesigns) => {
    const { activeAnalysisSetGuid } = analysisSets;
    const activeAnalysisSet = analysisSets.sets.find((analysisSet) => analysisSet.guid === activeAnalysisSetGuid);
    return (
      activeAnalysisSet &&
      activeAnalysisSet.componentDesign === componentDesigns[activeAnalysisSet.componentDesign.guid]
    );
  },
);

export const activeAnalysisSetMiddleware: Middleware<{}, RootState, Dispatch<Action>> =
  (store) => (next) => (action) => {
    const oldState = store.getState();
    const oldActiveAnalysisSetGuid = oldState.analysisSets.activeAnalysisSetGuid;
    const oldStateAnalysisSet = oldState.analysisSets.sets.find((set) => set.guid === oldActiveAnalysisSetGuid);
    const oldStateComponentDesignGuid = oldStateAnalysisSet?.componentDesign.guid;
    const oldComponentDesignHasWorkingChanges =
      oldStateComponentDesignGuid &&
      createDoWorkingChangesExistOnComponentDesign(oldStateComponentDesignGuid)(oldState);

    const oldActiveAnalysisSetIsCurrent = isActiveAnalysisSetCurrentSelector(oldState);

    next(action);

    const newState = store.getState();
    const newActiveAnalysisSetGuid = newState.analysisSets.activeAnalysisSetGuid;
    const newStateAnalysisSet = newState.analysisSets.sets.find((set) => set.guid === newActiveAnalysisSetGuid);
    const newStateComponentDesignGuid = newStateAnalysisSet?.componentDesign.guid;
    const newComponentDesignHasWorkingChanges =
      newStateComponentDesignGuid &&
      createDoWorkingChangesExistOnComponentDesign(newStateComponentDesignGuid)(newState);

    const activeAnalysisSetIsNoLongerCurrent =
      oldActiveAnalysisSetIsCurrent && !isActiveAnalysisSetCurrentSelector(newState);
    const componentDesignHasNewWorkingChanges =
      !oldComponentDesignHasWorkingChanges && newComponentDesignHasWorkingChanges;

    if (
      newActiveAnalysisSetGuid &&
      oldActiveAnalysisSetGuid === newActiveAnalysisSetGuid &&
      // These could theoretically differ if the analysis set GUID is set, but the analysis set is not loaded yet
      oldStateComponentDesignGuid === newStateComponentDesignGuid &&
      (activeAnalysisSetIsNoLongerCurrent || componentDesignHasNewWorkingChanges)
    ) {
      store.dispatch(setWorkingChanges());
    }
  };
